import { useMemo, useState } from "react";
import { getEmployeeList } from "src/apis/employeeApi";
import { CodeForm } from "src/components/common/form/CodeForm";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { SingleDateForm } from "src/components/common/form/SingleDateForm";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { formatDate } from "src/utils/string/stringUtil";
import {
	defaultKSK2040_R01HeaderDto,
	KSK2040_R01_INSPECT_TYPE,
} from "./Dto_KSK2040_R01";
import { useKSK2040_R01Store } from "./Store_KSK2040_R01";

interface InfoKSK2040R01Props {
	handleInspectTypeChange: (inspectType: string) => void;
}

export const Info_KSK2040_R01 = ({
	handleInspectTypeChange,
}: InfoKSK2040R01Props) => {
	const { setCodeSearchModalProps } = useUiuxStore();
	const { inspectionTypeList } = useCommonCodeStore();
	const {
		selectedKSK2040_R01,
		editKSK2040_R01,
		setEditKSK2040_R01,
	} = useKSK2040_R01Store();
	const [dateVisible, setDateVisible] = useState(false);
	const [datePickerPos, setDatePickerPos] = useState({ top: 0, left: 0, height: 0 });
	const fmlTypeList = useMemo(
		() => inspectionTypeList.filter((item) => item.ref01 === KSK2040_R01_INSPECT_TYPE),
		[inspectionTypeList],
	);

	const infoFieldConfigs = useMemo<InfoCodeSearchFieldConfig[]>(() => [
		{
			fieldName: "inspectorEmp",
			category: "담당자",
			focusId: "KSK2040_R01-inspector",
			responseMapping: { empNo: "inspectorEmp", empNm: "inspectorEmpNm" },
			searchFunction: async (code: string) => {
				const response = await getEmployeeList("all");
				return response.data.data.find((employee) => employee.empNo === code);
			},
		},
		{
			fieldName: "approvorEmpno",
			category: "담당자",
			focusId: "KSK2040_R01-approver",
			responseMapping: { empNo: "approvorEmpno", empNm: "approvorEmpNm" },
			searchFunction: async (code: string) => {
				const response = await getEmployeeList("all");
				return response.data.data.find((employee) => employee.empNo === code);
			},
		},
	], []);

	const { getFieldHandlers } = useInfoFormEvents({
		editData: editKSK2040_R01,
		setEditData: setEditKSK2040_R01,
		defaultData: defaultKSK2040_R01HeaderDto,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: "KSK2040_R01",
	});

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300 bg-white">
			<InputForm title="사업장" name="compNm" value={editKSK2040_R01.compNm} editable={false} />
			<InputForm title="작업장" name="workplaceNm" value={editKSK2040_R01.workplaceNm} editable={false} />
			<DropDownForm
				title="검사구분"
				name="inspectType"
				data={fmlTypeList}
				value={editKSK2040_R01.inspectType}
				initLabel="선택"
				editable={Boolean(selectedKSK2040_R01)}
				onChange={(event) => handleInspectTypeChange(event.target.value)}
			/>
			<CodeForm
				label="검사자"
				value={editKSK2040_R01.inspectorEmp}
				custNmValue={editKSK2040_R01.inspectorEmpNm}
				editable={Boolean(selectedKSK2040_R01)}
				handleFoucsId="KSK2040_R01-inspector"
				name="inspectorEmp"
				handleChange={getFieldHandlers("inspectorEmp").onChange}
				onDoubleClick={getFieldHandlers("inspectorEmp").onDoubleClick}
				onKeyDown={getFieldHandlers("inspectorEmp").onEnterKeyDown}
			/>
			<CodeForm
				label="승인자"
				value={editKSK2040_R01.approvorEmpno}
				custNmValue={editKSK2040_R01.approvorEmpNm}
				editable={Boolean(selectedKSK2040_R01)}
				handleFoucsId="KSK2040_R01-approver"
				name="approvorEmpno"
				handleChange={getFieldHandlers("approvorEmpno").onChange}
				onDoubleClick={getFieldHandlers("approvorEmpno").onDoubleClick}
				onKeyDown={getFieldHandlers("approvorEmpno").onEnterKeyDown}
			/>
			<SingleDateForm
				title="검사일자"
				value={editKSK2040_R01.inspectDt ? new Date(editKSK2040_R01.inspectDt) : undefined}
				setValue={(inspectDt) => setEditKSK2040_R01({ ...editKSK2040_R01, inspectDt: formatDate(inspectDt) })}
				editable={Boolean(selectedKSK2040_R01)}
				visible={dateVisible}
				setVisible={setDateVisible}
				pickerPos={datePickerPos}
				setPickerPos={setDatePickerPos}
			/>
		</div>
	);
};
